function formValue(form: FormData, key: string) {
  const value = form.get(key);
  return typeof value === 'string' ? value : '';
}
import { OperationsRecovery } from './operations-recovery';
import { retainOperationsMutation, settleOperationsMutation } from '../../lib/operations-pending';
import { useEffect, useState, type SyntheticEvent } from 'react';
import {
  operationsWorkspaceSchema,
  type OperationsWorkspace,
  type OperationsCommand,
} from '@sqi/shared-schemas';
import { request, type OemWorkspace, OemApiError } from '../../lib/oem-client';
import type { OemContext } from '../../lib/auth';
export function StockWorkspace({
  context,
  workspace,
  userId,
}: {
  context: OemContext;
  workspace: OemWorkspace;
  userId: string;
}) {
  const [data, setData] = useState<OperationsWorkspace | null>(null),
    [message, setMessage] = useState('กำลังตรวจสิทธิ์ปฏิบัติการ'),
    [busy, setBusy] = useState(false);
  const [selection, setSelection] = useState('');
  const refresh = async () => {
    setData(null);
    setSelection('');
    const raw = await request(`/operations/${context.organizationId}/${context.projectId}`);
    setData(operationsWorkspaceSchema.parse(raw));
    setMessage('อ่านข้อมูลที่บันทึกแล้ว');
  };
  useEffect(() => {
    let active = true;
    void request(`/operations/${context.organizationId}/${context.projectId}`)
      .then((raw) => {
        if (active) {
          setData(operationsWorkspaceSchema.parse(raw));
          setMessage('อ่านข้อมูลที่บันทึกแล้ว');
        }
      })
      .catch(() => {
        if (active) {
          setData(null);
          setSelection('');
          setMessage('ไม่มีสิทธิ์ปฏิบัติการที่ยังใช้งานได้ หรือยังเชื่อมต่อไม่ได้');
        }
      });
    return () => {
      active = false;
    };
  }, [context.organizationId, context.projectId]);
  const run = async (payload: OperationsCommand) => {
    setBusy(true);
    const scope = {
      actorId: userId,
      organizationId: context.organizationId,
      projectId: context.projectId,
      action: payload.action,
    };
    try {
      if (!navigator.onLine) throw new Error('online_required');
      const key = await retainOperationsMutation(sessionStorage, scope, payload);
      await request('/operations', {
        organizationId: context.organizationId,
        projectId: context.projectId,
        idempotencyKey: key,
        payload,
      });
      settleOperationsMutation(sessionStorage, scope, key);
      await refresh();
    } catch (e) {
      setMessage(
        e instanceof OemApiError
          ? `บันทึกไม่ได้ (${e.code}) · ตรวจสิทธิ์ หน่วย จำนวน และหลักฐาน`
          : 'บันทึกไม่ได้ โปรดตรวจข้อมูลและเชื่อมต่ออีกครั้ง',
      );
    } finally {
      setBusy(false);
    }
  };
  const submit = (event: SyntheticEvent<HTMLFormElement>, action: 'purchase_draft' | 'receive') => {
    event.preventDefault();
    const f = new FormData(event.currentTarget),
      get = (key: string) => formValue(f, key);
    const base = {
      ownerCustomerId: get('owner') === 'customer' ? context.customerId : null,
      specificationId: get('specificationId'),
      supplierReference: get('supplierReference'),
      quantity: get('quantity'),
      unit: get('unit') as 'g' | 'ml' | 'ea',
      evidence: get('evidence'),
    };
    if (action === 'purchase_draft') void run({ action, ...base });
    else {
      const date = new Date(get('expiresAt') + 'T23:59:59+07:00');
      if (!Number.isFinite(date.getTime())) {
        setMessage('วันหมดอายุไม่ถูกต้อง');
        return;
      }
      void run({
        action,
        ...base,
        purchaseDraftId: get('purchaseDraftId') || null,
        facility: get('facility'),
        location: get('location'),
        label: get('label'),
        receiptReference: get('receiptReference'),
        expiresAt: date.toISOString(),
      });
    }
  };
  const specs = data?.specifications ?? [];
  const lot = data?.lots.find((l) => l.id === selection);
  const capabilities = data?.grants.map((g) => g.capability) ?? [];
  return (
    <>
      <h1>คลังวัตถุดิบและจัดซื้อ</h1>
      <p>แยกทรัพย์สินโรงงานและลูกค้า · ทะเบียนส่วนตัวโรงงาน · ร่างจัดซื้อไม่ส่งคำสั่งซื้อ</p>
      <p role="status">{message}</p>
      <button
        className="action secondary"
        disabled={busy}
        onClick={() => {
          void refresh().catch(() => setMessage('ยังอ่านข้อมูลไม่ได้'));
        }}
      >
        โหลดข้อมูลที่บันทึกอีกครั้ง
      </button>
      <OperationsRecovery context={context} userId={userId} onRecovered={refresh} />
      {data ? (
        <>
          {capabilities.includes('warehouse')
            ? (['purchase_draft', 'receive'] as const).map((action) => (
                <form className="card form-grid" key={action} onSubmit={(e) => submit(e, action)}>
                  <h2>{action === 'purchase_draft' ? 'ร่างจัดซื้อ' : 'รับสินค้าเข้ากักกัน'}</h2>
                  <label>
                    เจ้าของ
                    <select name="owner">
                      <option value="factory">โรงงาน</option>
                      <option value="customer">ลูกค้าในโครงการนี้</option>
                    </select>
                  </label>
                  <label>
                    สเปกที่ทบทวนแล้ว
                    <select name="specificationId" required>
                      {specs.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label} · {s.hash.slice(0, 8)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    แหล่งผู้ขาย / เอกสารผู้ขาย
                    <input name="supplierReference" required maxLength={1000} />
                  </label>
                  <label>
                    จำนวน (ไม่ใช้เลขยกกำลัง)
                    <input
                      name="quantity"
                      inputMode="decimal"
                      pattern="[0-9]+(\.[0-9]{1,6})?"
                      required
                    />
                  </label>
                  <label>
                    หน่วยฐาน
                    <select name="unit">
                      <option value="g">กรัม (g)</option>
                      <option value="ml">มิลลิลิตร (ml)</option>
                      <option value="ea">ชิ้น (ea)</option>
                    </select>
                  </label>
                  {action === 'receive' ? (
                    <>
                      <label>
                        ร่างจัดซื้อ (ถ้ามี)
                        <select name="purchaseDraftId">
                          <option value="">ไม่มีร่างจัดซื้อที่ผูก</option>
                          {data.purchaseDrafts.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.id.slice(0, 8)} · {d.quantity} {d.unit}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        ชื่อล็อต
                        <input name="label" required maxLength={200} />
                      </label>
                      <label>
                        โรงงาน / สถานที่
                        <input name="facility" required maxLength={200} />
                      </label>
                      <label>
                        ตำแหน่งเก็บ
                        <input name="location" required maxLength={200} />
                      </label>
                      <label>
                        เลขเอกสารรับเข้า
                        <input name="receiptReference" required maxLength={1000} />
                      </label>
                      <label>
                        หมดอายุ
                        <input name="expiresAt" type="date" required />
                      </label>
                    </>
                  ) : null}
                  <label>
                    หลักฐานและเหตุผล
                    <textarea name="evidence" required maxLength={4000} />
                  </label>
                  <button className="action" disabled={busy || !specs.length}>
                    บันทึก{action === 'purchase_draft' ? 'ร่าง' : 'รับเข้า'}
                  </button>
                </form>
              ))
            : null}
          <section className="card">
            <h2>ล็อตที่มีสิทธิ์ดู</h2>
            <label>
              เลือกล็อต
              <select value={selection} onChange={(e) => setSelection(e.target.value)}>
                <option value="">เลือกรายการ</option>
                {data.lots.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.label} · {l.qaState} · {l.balance.onHand} {l.unit}
                  </option>
                ))}
              </select>
            </label>
            {lot ? (
              <>
                <p>
                  เจ้าของ: {lot.ownerCustomerId ? 'ลูกค้า' : 'โรงงาน'} · {lot.facility} /{' '}
                  {lot.location}
                </p>
                <p>
                  คงเหลือ {lot.balance.onHand} {lot.unit} · จอง {lot.balance.reserved} {lot.unit} ·
                  QA {lot.qaState}
                </p>
                <p>หมดอายุ {new Date(lot.expiresAt).toLocaleDateString('th-TH')}</p>
                {capabilities.includes('lot_qa') ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const f = new FormData(e.currentTarget);
                      void run({
                        action: 'lot_qa',
                        lotId: lot.id,
                        disposition: formValue(f, 'disposition') as
                          'accepted' | 'rejected' | 'quarantine',
                        evidence: formValue(f, 'evidence'),
                      });
                    }}
                  >
                    <h3>บันทึกผลตรวจล็อต</h3>
                    <p>ผู้ตรวจต้องเป็นคนละคนกับผู้รับเข้าและผู้ทำรายการล็อต</p>
                    <label>
                      ผลตรวจ
                      <select name="disposition">
                        <option value="quarantine">กักกัน</option>
                        <option value="accepted">ยอมรับตามหลักฐานตรวจ</option>
                        <option value="rejected">ไม่ยอมรับ</option>
                      </select>
                    </label>
                    <label>
                      หลักฐานการตรวจ
                      <textarea name="evidence" required maxLength={4000} />
                    </label>
                    <button className="action" disabled={busy}>
                      บันทึกผลตรวจ
                    </button>
                  </form>
                ) : null}
                {capabilities.includes('warehouse') ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const f = new FormData(e.currentTarget),
                        action = formValue(f, 'action'),
                        base = {
                          lotId: lot.id,
                          quantity: formValue(f, 'quantity'),
                          unit: lot.unit,
                          evidence: formValue(f, 'evidence'),
                        };
                      if (action === 'reserve' && workspace.order)
                        void run({ action, ...base, orderId: workspace.order.id });
                      else if (action === 'unreserve')
                        void run({ action, ...base, reservationId: formValue(f, 'referenceId') });
                      else if (action === 'correct_in' || action === 'correct_out')
                        void run({
                          action,
                          ...base,
                          referenceEventId: formValue(f, 'referenceId'),
                        });
                      else if (action === 'damage' || action === 'return_supplier')
                        void run({ action, ...base });
                    }}
                  >
                    <h3>จอง คืน และแก้ไข</h3>
                    <label>
                      รายการ
                      <select name="action">
                        <option value="reserve" disabled={!workspace.order}>
                          จองให้คำสั่งซื้อที่ยืนยันในโครงการนี้
                        </option>
                        <option value="unreserve">ยกเลิกการจองบางส่วน</option>
                        <option value="damage">ชำรุด</option>
                        <option value="return_supplier">คืนผู้ขาย</option>
                        <option value="correct_in">แก้รับขาด (กลับเข้ากักกัน)</option>
                        <option value="correct_out">แก้รับเกิน</option>
                      </select>
                    </label>
                    <label>
                      จำนวน ({lot.unit})<input name="quantity" required inputMode="decimal" />
                    </label>
                    <label>
                      รหัสรายการอ้างอิง / การจอง
                      <input name="referenceId" />
                    </label>
                    <label>
                      หลักฐานและเหตุผล
                      <textarea name="evidence" required maxLength={4000} />
                    </label>
                    <button className="action" disabled={busy}>
                      บันทึกการเคลื่อนไหว
                    </button>
                  </form>
                ) : null}
                <h3>ประวัติที่แก้ทับไม่ได้</h3>
                <ol>
                  {lot.events.map((e) => (
                    <li key={e.id}>
                      {e.event} · {e.quantity} {lot.unit} · <code>{e.reservation_id ?? e.id}</code>
                    </li>
                  ))}
                </ol>
              </>
            ) : (
              <p>ยังไม่ได้เลือกล็อต</p>
            )}
          </section>
        </>
      ) : null}
    </>
  );
}
